import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./starRatings.html";
/*
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import { i18nStore, WireI18n } from '~/modules/stores/i18nStore';
import { throwDevelopmentOrLog } from '~/modules/utilities/cross_env_utils';
const NUMBER_OF_STARS = 5;
const ALLOWED_ACTION_KEYS = ['Minus', 'ArrowDown', 'ArrowLeft', 'Plus', 'ArrowRight', 'ArrowUp', 'Backspace', 'Delete', 'Digit0'];
export let StarSize = /*#__PURE__*/function (StarSize) {
  StarSize["XSSmall"] = "xsmall";
  StarSize["Small"] = "small";
  StarSize["Medium"] = "medium";
  StarSize["Large"] = "large";
  StarSize["XLarge"] = "xlarge";
  StarSize["XXLarge"] = "xxlarge";
  return StarSize;
}({});
export let LabelPosition = /*#__PURE__*/function (LabelPosition) {
  LabelPosition["Top"] = "top";
  LabelPosition["Left"] = "left";
  LabelPosition["Right"] = "right";
  LabelPosition["Bottom"] = "bottom";
  return LabelPosition;
}({});
class StarRating extends LightningElement {
  constructor(...args) {
    super(...args);
    this.labelPosition = LabelPosition.Left;
    this.size = StarSize.Medium;
    this.spaceBetween = 'small';
    this.forceShowError = false;
    this.ariaLabel = null;
    this.required = null;
    this.reviewCount = null;
    this.ratingLabel = null;
    // When linkReviews is true (listingDetail), a slot where a wds-link component can be passed in will be rendered instead of the reviewCount number
    this.linkReviews = false;
    this._value = 0;
    this.readOnly = false;
    this.rating = 0;
    this.calculateRoundedRating = () => {
      // Read-only ratings render fractional fill via ax-icon's gradient.
      // Interactive ratings round to whole stars since users can only pick a whole value.
      this.rating = this.readOnly ? this.value : Math.round(this.value);
    };
    this.isDigitKeyEventCode = function (eventCode) {
      return eventCode.indexOf('Digit') === 0;
    };
    this.handleDigits = eventCode => {
      const dStr = 'Digit';
      const digitPressed = parseInt(eventCode.substr(dStr.length, eventCode.length - 1), 10);
      this.setRating(digitPressed);
      this.changeInputFocus(digitPressed);
    };
    this.getKeyHandler = keyEventCode => {
      const keyHandlers = {
        Minus: this.decrement,
        Plus: this.increment,
        ArrowDown: this.increment,
        ArrowRight: this.increment,
        ArrowUp: this.decrement,
        ArrowLeft: this.decrement,
        Backspace: this.reset,
        Delete: this.reset,
        Digit0: this.reset
      };
      keyHandlers[keyEventCode]?.();
    };
    this.increment = () => {
      const newRating = this.rating + 1;
      this.setRating(newRating > NUMBER_OF_STARS ? 1 : newRating);
      this.changeInputFocus(this.rating);
    };
    this.decrement = () => {
      const newRating = this.rating - 1;
      this.setRating(newRating <= 0 ? NUMBER_OF_STARS : newRating);
      this.changeInputFocus(this.rating);
    };
    this.reset = () => {
      this.setRating(0);
      this.changeInputFocus(1);
    };
    this.changeInputFocus = dataRating => {
      let elementToFocus = null;
      if (dataRating <= 0) {
        elementToFocus = this.template.querySelector(`input[data-rating="${NUMBER_OF_STARS}"]`);
      }
      if (dataRating > NUMBER_OF_STARS) {
        elementToFocus = this.template.querySelector(`input[data-rating="1"]`);
      }
      if (dataRating > 0 && dataRating <= NUMBER_OF_STARS) {
        elementToFocus = this.template.querySelector(`input[data-rating="${dataRating}"]`);
      }
      if (elementToFocus) {
        elementToFocus.focus();
      } else {
        throwDevelopmentOrLog(new Error(`Failed to find input with '${dataRating}'`));
      }
    };
  }
  get value() {
    return this._value;
  }
  // Recompute the cached fill whenever `value` changes so an already-mounted
  // instance updates (e.g. editing a review reuses the same element keyed by
  // review id, so connectedCallback does not re-fire). See W-23575205.
  set value(val) {
    if (val === this._value) {
      return;
    }
    this._value = val;
    this.calculateRoundedRating();
  }
  i18nStoreCallback(i18n) {
    this.labels = i18n.starRatings;
  }
  connectedCallback() {
    this.calculateRoundedRating();
  }
  get isSingleReview() {
    return this.reviewCount === null;
  }
  get showNoReviewsText() {
    return this.readOnly && !this.value && !this.reviewCount;
  }
  get noReviewsText() {
    return this.labels.noReviews;
  }
  get containerRole() {
    return this.readOnly ? 'img' : 'radiogroup';
  }
  get spaceBetweenClass() {
    return 'space-' + this.spaceBetween;
  }
  get ratingAsInteger() {
    return Math.floor(this.rating);
  }
  get starsTabIndex() {
    return this.readOnly ? '-1' : '0';
  }
  get componentClassNames() {
    return ['rating', this.rating ? `rating-${this.ratingAsInteger}` : 'rating-0', this.size, this.readOnly ? 'read-only' : '', `label-${this.labelPosition}`, this.spaceBetweenClass].join(' ');
  }
  get starsArray() {
    const stars = [];
    for (let i = 0; i < NUMBER_OF_STARS; i++) {
      const star = {
        id: 'star-' + (i + 1),
        value: i + 1,
        checked: i + 1 === this.value ? true : false,
        ariaLabel: i18nStore.getState().i18n.starRatings.ariaLabelStar({
          CURRENT_STAR: i + 1,
          NUMBER_OF_STARS: NUMBER_OF_STARS
        }),
        gradient: 100
      };
      if (i >= this.rating) {
        star.gradient = 0;
      } else if (i === this.ratingAsInteger && this.rating % 1 !== 0) {
        star.gradient = Math.round(this.rating % 1 * 100);
      }
      stars.push(star);
    }
    return stars;
  }
  get ariaLabelText() {
    return this.ariaLabel || i18nStore.getState().i18n.starRatings.ariaLabelStar({
      CURRENT_STAR: this.value,
      NUMBER_OF_STARS: NUMBER_OF_STARS
    });
  }
  onStarClicked(event) {
    if (!this.interactionPossible()) {
      return;
    }

    // Get the HTML element that was clicked
    const target = event.target;
    if (!target) {
      return;
    }

    // Get the closest radio button element to the clicked element
    const starRadio = target.closest('[data-rating]');
    const ratingValue = starRadio ? parseInt(starRadio.getAttribute('data-rating'), 10) : 0;
    this.setRating(ratingValue);
  }

  // This method is triggered whenever the rating value changes through user interactions with the input components.
  // It handles actions like clicks or keyboard accessibility actions to update the rating value accordingly.
  setRating(rating) {
    let newRating = rating;
    // Ensure rating is within the valid range
    if (rating < 0) {
      newRating = 0;
    }
    if (rating > NUMBER_OF_STARS) {
      newRating = NUMBER_OF_STARS;
    }
    this.rating = newRating;
    this.template.querySelector('.star-container')?.setAttribute('aria-activedescendant', `star-${newRating}`);

    // Dispatches a 'change' event after every rating value change to notify listeners about updates in the rating value.
    this.dispatchEvent(new CustomEvent('change', {
      detail: {
        rating: this.rating
      }
    }));
  }
  interactionPossible() {
    return !this.readOnly;
  }
  onKeyDown(event) {
    if (!this.interactionPossible()) {
      return;
    }
    const code = event.code;
    const isAllowedKey = ALLOWED_ACTION_KEYS.includes(code);
    const isDigitKeyEvent = this.isDigitKeyEventCode(code);
    if (isAllowedKey || isDigitKeyEvent) {
      if (isDigitKeyEvent) {
        this.handleDigits(code);
      } else {
        this.getKeyHandler(code);
      }
      event.preventDefault();
      event.stopPropagation();
    }
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(StarRating, {
  publicProps: {
    labelPosition: {
      config: 0
    },
    size: {
      config: 0
    },
    spaceBetween: {
      config: 0
    },
    forceShowError: {
      config: 0
    },
    ariaLabel: {
      config: 0
    },
    required: {
      config: 0
    },
    reviewCount: {
      config: 0
    },
    ratingLabel: {
      config: 0
    },
    linkReviews: {
      config: 0
    },
    value: {
      config: 3
    },
    readOnly: {
      config: 0
    }
  },
  wire: {
    i18nStoreCallback: {
      adapter: WireI18n,
      method: 1,
      config: function ($cmp) {
        return {};
      }
    }
  },
  fields: ["_value", "rating", "calculateRoundedRating", "isDigitKeyEventCode", "handleDigits", "getKeyHandler", "increment", "decrement", "reset", "changeInputFocus"]
});
const __lwc_component_class_internal = _registerComponent(StarRating, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;